import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  IconAlertTriangle,
  IconCellSignalOff,
  IconCircleCheck,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import classNames from "classnames";
import s from "./appointment-wrapper.module.scss";

const NOTIFICATION_ICONS = {
  error: IconAlertTriangle,
  success: IconCircleCheck,
  info: IconInfoCircle,
	network: IconCellSignalOff
};

const AppointmentNotificationContext = createContext(null);

const NOTIFICATION_ANIMATION = {
  initial: { opacity: 0, y: -16, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -12, scale: 0.98 },
  transition: { duration: 0.2, ease: "easeOut" },
};

export const AppointmentNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef(new Map());

  const removeNotification = useCallback((id) => {
    const timerId = timersRef.current.get(id);

    if (timerId) {
      clearTimeout(timerId);
      timersRef.current.delete(id);
    }

    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const addNotification = useCallback(
    (notification) => {
      const normalizedNotification =
        typeof notification === "string" ? { message: notification } : notification;

      const id =
        normalizedNotification?.id ||
        (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

      const duration = normalizedNotification?.duration ?? 6000;

      setNotifications((prev) => {
        const nextNotification = {
          type: "error",
          duration,
          ...normalizedNotification,
          id,
        };

        const hasExistingNotification = prev.some(
          (currentNotification) => currentNotification.id === id
        );

        if (hasExistingNotification) {
          return prev.map((currentNotification) =>
            currentNotification.id === id ? nextNotification : currentNotification
          );
        }

        return [...prev, nextNotification];
      });

      const existingTimerId = timersRef.current.get(id);

      if (existingTimerId) {
        clearTimeout(existingTimerId);
        timersRef.current.delete(id);
      }

      if (duration > 0) {
        const timerId = setTimeout(() => {
          removeNotification(id);
        }, duration);

        timersRef.current.set(id, timerId);
      }

      return id;
    },
    [removeNotification]
  );

  const clearNotifications = useCallback(() => {
    timersRef.current.forEach((timerId) => clearTimeout(timerId));
    timersRef.current.clear();
    setNotifications([]);
  }, []);

  useEffect(() => () => {
    timersRef.current.forEach((timerId) => clearTimeout(timerId));
    timersRef.current.clear();
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      addNotification,
      removeNotification,
      clearNotifications,
    }),
    [addNotification, clearNotifications, notifications, removeNotification]
  );

  return (
    <AppointmentNotificationContext.Provider value={value}>
      {children}
      <div className={s.notificationsViewport}>
        <AnimatePresence initial={false}>
          {notifications.map((notification) => {
            const notificationType = notification.type || "error";
            const NotificationIcon = NOTIFICATION_ICONS[notificationType] || IconAlertTriangle;

            return (
              <motion.div
                key={notification.id}
                className={s.notification}
                initial={NOTIFICATION_ANIMATION.initial}
                animate={NOTIFICATION_ANIMATION.animate}
                exit={NOTIFICATION_ANIMATION.exit}
                transition={NOTIFICATION_ANIMATION.transition}
              >
                <div
                  className={classNames(
                    s.notificationContent,
                    s[notificationType]
                  )}
                >
                  <NotificationIcon className={s.notificationIcon} size={18} stroke={2} />
                  <span className={s.notificationMessage}>{notification.message}</span>
                  {notification.action ? (
                    <button
                      type="button"
                      className={s.notificationAction}
                      onClick={notification.action.onClick}
                    >
                      {notification.action.label}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={s.notificationClose}
                    onClick={() => removeNotification(notification.id)}
                    aria-label="Закрыть уведомление"
                  >
                    <IconX size={16} stroke={2} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </AppointmentNotificationContext.Provider>
  );
};

export const useAppointmentNotification = () => {
  const context = useContext(AppointmentNotificationContext);

  if (!context) {
    throw new Error(
      "useAppointmentNotification must be used within AppointmentNotificationProvider"
    );
  }

  return context;
};
